import { useMutation, useQuery } from '@apollo/client/react'

import {
  ADVISE_CATEGORY_SKILLS,
  HEADLINE_MAX,
  MAX_CATEGORY_BLOCKS,
  NOTE_MAX,
} from '@src/components/sco/Advisors/data/becomeAdvisor'
import {
  type AdvisorApplicationValues,
  type ApplyExperience,
  type ApplyUserSkill,
  derivedNotableBrands,
  derivedSkillTagTitles,
  emptyExperience,
  fillBlank,
  managedSkillTitles,
  matchUserSkillByTitle,
  MAX_BRANDS,
  MAX_EXPERIENCES,
  mergeAdvisorRoleKeys,
  mergeExtractedApplicationValues,
  platformCompensationKeys,
  titlesToDelete,
  valuesFromProfile,
  valuesFromUserAndProfile,
  type ExtractedApplicationFields,
} from '@src/components/sco/Advisors/sections/advisorApplication/applyProfileMapping'
import { adviseTopicsToCategoryKeys } from '@src/components/sco/Advisors/utils/categoryKeys'
import { clipAtWord } from '@src/components/sco/Advisors/utils/clipAtWord'
import { LocationInputPlace } from '@src/components/sco/LocationInput'
import { UPDATE_PROFILE } from '@src/gql/mutations/UpdateProfile'
import { UPSERT_ADVISOR_PROFILE } from '@src/gql/mutations/UpsertAdvisorProfile'
import { UPSERT_USER_EXPERIENCE } from '@src/gql/mutations/UpsertUserExperience'
import { UPSERT_USER_SKILL } from '@src/gql/mutations/UpsertUserSkill'
import { ADVISOR_APPLY_USER_PROFILE } from '@src/gql/queries/AdvisorApplyUserProfile'
import { MY_ADVISOR_PROFILE } from '@src/gql/queries/MyAdvisorProfile'
import { ScoAdvisorProfile, ScoUser, ScoUserExperience } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { prettyErrorMessage } from '@src/lib/mutationErrors'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export { MAX_BRANDS, MAX_EXPERIENCES, valuesFromProfile, valuesFromUserAndProfile, emptyExperience }
export type { AdvisorApplicationValues, ApplyExperience }

/** Warm-intro labels ↔ keys (`advisor-warm-intros` on the API). */
export const WARM_INTRO_OPTIONS = [
  { key: 'customers', label: 'Customers' },
  { key: 'investors', label: 'Investors' },
  { key: 'talent', label: 'Talent' },
] as const

export type AboutYouValues = {
  firstName: string
  lastName: string
  email: string
  pictureUrl: string | null
  locationTitle: string
  locationPlace: LocationInputPlace | null
}

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

/** Rehydrate About You from the signed-in User Profile. */
export const aboutYouFromUser = (user?: Partial<ScoUser> | null): AboutYouValues => ({
  firstName: user?.firstName || '',
  lastName: user?.lastName || '',
  email: user?.email || '',
  pictureUrl: user?.pictureUrl || null,
  locationTitle: user?.location || '',
  locationPlace: user?.location ? { placeId: 'default', title: user.location } : null,
})

/** Fill blank About You fields from a later currentUser payload (Clarity claim/extract). */
export const mergeAboutYouFromUser = (
  prev: AboutYouValues,
  user?: Partial<ScoUser> | null
): AboutYouValues => {
  if (!user) {
    return prev
  }
  const incoming = aboutYouFromUser(user)
  return {
    firstName: fillBlank(prev.firstName, incoming.firstName),
    lastName: fillBlank(prev.lastName, incoming.lastName),
    email: fillBlank(prev.email, incoming.email),
    pictureUrl: prev.pictureUrl || incoming.pictureUrl,
    locationTitle: fillBlank(prev.locationTitle, incoming.locationTitle),
    locationPlace: prev.locationPlace || incoming.locationPlace,
  }
}

export const sameAboutYou = (a: AboutYouValues, b: AboutYouValues): boolean =>
  a.firstName === b.firstName &&
  a.lastName === b.lastName &&
  a.email === b.email &&
  a.pictureUrl === b.pictureUrl &&
  a.locationTitle === b.locationTitle &&
  (a.locationPlace?.placeId || '') === (b.locationPlace?.placeId || '') &&
  (a.locationPlace?.title || '') === (b.locationPlace?.title || '')

/**
 * Network-only queries can already have a cached stub (`hasCachedData`) while
 * `loading` is still true. Do not lock apply hydration until the network returns.
 */
export const isApplyQuerySettled = ({ loading }: { loading: boolean; hasCachedData?: boolean }): boolean =>
  !loading

export type ApplicationFieldErrors = {
  firstName?: string
  lastName?: string
  email?: string
  categories?: string
  skills?: string
  headline?: string
  compensationKeys?: string
  engagementStyleKeys?: string
}

export const firstFieldError = (errors: ApplicationFieldErrors): string | null =>
  Object.values(errors).find((message): message is string => !!message) ?? null

export const hasFieldErrors = (errors: ApplicationFieldErrors): boolean => !!firstFieldError(errors)

export const aboutYouFieldErrors = (about: AboutYouValues): ApplicationFieldErrors => {
  const errors: ApplicationFieldErrors = {}
  if (!about.firstName.trim()) {
    errors.firstName = 'Add your first name.'
  }
  if (!about.lastName.trim()) {
    errors.lastName = 'Add your last name.'
  }
  if (!about.email.trim()) {
    errors.email = 'Add the email we should use for advisor notifications.'
  } else if (!EMAIL_PATTERN.test(about.email.trim())) {
    errors.email = 'Enter a valid email address.'
  }
  return errors
}

export const validateAboutYou = (about: AboutYouValues): string | null =>
  firstFieldError(aboutYouFieldErrors(about))

export const isAboutYouComplete = (about: AboutYouValues): boolean => !validateAboutYou(about)

export const expertiseFieldErrors = (values: AdvisorApplicationValues): ApplicationFieldErrors => {
  const errors: ApplicationFieldErrors = {}
  if (!values.categories.length) {
    errors.categories = 'Pick at least one area you can advise on.'
  }
  if (!values.skills.length) {
    errors.skills = 'Add at least one skill so founders know what you cover.'
  }
  return errors
}

export const backgroundFieldErrors = (values: AdvisorApplicationValues): ApplicationFieldErrors =>
  values.headline.trim() ? {} : { headline: 'Add a one-line headline for your advisor profile.' }

export const advisingFieldErrors = (values: AdvisorApplicationValues): ApplicationFieldErrors => {
  const errors: ApplicationFieldErrors = {}
  if (!values.compensationKeys.length) {
    errors.compensationKeys = 'Choose how you would like to be compensated.'
  }
  if (!values.engagementStyleKeys.length) {
    errors.engagementStyleKeys = 'Choose at least one way founders can engage with you.'
  }
  return errors
}

export const fieldErrorsForStep = (
  key: 'about' | 'expertise' | 'background' | 'advising',
  aboutYou: AboutYouValues,
  values: AdvisorApplicationValues
): ApplicationFieldErrors => {
  switch (key) {
    case 'about':
      return aboutYouFieldErrors(aboutYou)
    case 'expertise':
      return expertiseFieldErrors(values)
    case 'background':
      return backgroundFieldErrors(values)
    case 'advising':
      return advisingFieldErrors(values)
  }
}

const samePlace = (a: LocationInputPlace | null, b: LocationInputPlace | null): boolean =>
  (a?.placeId || '') === (b?.placeId || '') && (a?.title || '') === (b?.title || '')

const EMPTY: AdvisorApplicationValues = {
  categories: [],
  skills: [],
  headline: '',
  experiences: [],
  note: '',
  compensationKeys: ['equity'],
  engagementStyleKeys: [],
  warmIntroKeys: [],
}

/** Order-sensitive list equality, used to skip no-op state writes. */
export const sameStringList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index])

type MyProfileData = { myAdvisorProfile?: ScoAdvisorProfile | null }

type ApplyUserData = {
  viewer?: {
    currentUser?: {
      shortBio?: string | null
      userSkills?: ApplyUserSkill[] | null
      userExperiences?: Array<Pick<
        ScoUserExperience,
        'userExperienceId' | 'organizationName' | 'experienceTypeKey' | 'positionDescription'
      > | null> | null
    } | null
  } | null
}

type SaveResult = { ok: boolean; statusKey: string | null }

type UpsertResult = {
  data?: { upsertAdvisorProfile?: { advisorProfile?: { statusKey?: string | null } | null } | null } | null
  error?: unknown
}

const readStatusKey = (
  result: UpsertResult,
  refetched: { data?: MyProfileData } | undefined,
  fallback?: string | null
) =>
  result.data?.upsertAdvisorProfile?.advisorProfile?.statusKey ??
  refetched?.data?.myAdvisorProfile?.statusKey ??
  fallback ??
  null

/** Category names whose preset skill lists are shown as quick-select chips. */
export const skillSuggestionsFor = (categories: string[]): string[] => {
  const seen = new Set<string>()
  const suggestions: string[] = []
  for (const category of categories) {
    for (const skill of ADVISE_CATEGORY_SKILLS[category] ?? []) {
      if (!seen.has(skill)) {
        seen.add(skill)
        suggestions.push(skill)
      }
    }
  }
  return suggestions
}

/**
 * Advisor application state.
 *
 * Skills, categories, and compensation write through to UserSkill. Notable
 * experiences write UserExperience. Headline writes User.shortBio. The founder
 * note stays on AdvisorProfile.advisingNote. bioForAdvising is not written.
 */
export const useAdvisorApplication = () => {
  const { currentUser, refetchCurrentUser } = useCurrentUser()
  const [values, setValues] = useState<AdvisorApplicationValues>(EMPTY)
  const [aboutYou, setAboutYou] = useState<AboutYouValues>(() => aboutYouFromUser(currentUser))
  const [aboutHydrated, setAboutHydrated] = useState(!!currentUser)
  const [hydrated, setHydrated] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hydratedRef = useRef(false)
  const dirtyRef = useRef(false)
  const aboutHydratedRef = useRef(!!currentUser)
  const aboutDirtyRef = useRef(false)
  const previousManagedTitlesRef = useRef<string[]>([])
  const previousExperienceIdsRef = useRef<string[]>([])
  const userSkillsRef = useRef<ApplyUserSkill[]>([])

  const profileQuery = useQuery<MyProfileData>(MY_ADVISOR_PROFILE, { fetchPolicy: 'network-only' })
  const userProfileQuery = useQuery<ApplyUserData>(ADVISOR_APPLY_USER_PROFILE, {
    fetchPolicy: 'network-only',
  })
  const [upsertAdvisorProfile, upsertMutation] = useMutation(UPSERT_ADVISOR_PROFILE)
  const [updateProfile, updateProfileMutation] = useMutation(UPDATE_PROFILE)
  const [upsertUserSkill, upsertUserSkillMutation] = useMutation(UPSERT_USER_SKILL)
  const [upsertUserExperience, upsertUserExperienceMutation] = useMutation(UPSERT_USER_EXPERIENCE)

  const profile = profileQuery.data?.myAdvisorProfile
  const applyUser = userProfileQuery.data?.viewer?.currentUser

  useEffect(() => {
    userSkillsRef.current = (applyUser?.userSkills || []).filter(skill => !skill?.deletedAt)
  }, [applyUser])

  useEffect(() => {
    if (
      !isApplyQuerySettled({ loading: profileQuery.loading, hasCachedData: !!profile }) ||
      !isApplyQuerySettled({ loading: userProfileQuery.loading, hasCachedData: !!applyUser })
    ) {
      return
    }
    if (dirtyRef.current) {
      if (!hydratedRef.current) {
        hydratedRef.current = true
        setHydrated(true)
      }
      return
    }
    const next = valuesFromUserAndProfile({
      profile,
      userSkills: applyUser?.userSkills,
      userExperiences: applyUser?.userExperiences,
      shortBio: applyUser?.shortBio || currentUser?.shortBio,
    })
    if (!next.headline && currentUser) {
      const seed = [currentUser.title, currentUser.company?.name].filter(Boolean).join(' at ')
      if (seed) {
        next.headline = clipAtWord(seed, HEADLINE_MAX)
      }
    }
    hydratedRef.current = true
    setValues(next)
    previousManagedTitlesRef.current = managedSkillTitles(next.categories, next.skills)
    previousExperienceIdsRef.current = next.experiences
      .map(experience => experience.userExperienceId)
      .filter((id): id is string => !!id)
    setHydrated(true)
  }, [profile, profileQuery.loading, applyUser, userProfileQuery.loading, currentUser])

  useEffect(() => {
    if (!currentUser || aboutDirtyRef.current) {
      return
    }
    setAboutYou(prev => {
      const next = mergeAboutYouFromUser(prev, currentUser)
      return sameAboutYou(prev, next) ? prev : next
    })
    aboutHydratedRef.current = true
    setAboutHydrated(true)
  }, [currentUser])

  const applyExtracted = useCallback(
    async (
      extracted: ExtractedApplicationFields & {
        firstName?: string | null
        lastName?: string | null
        location?: string | null
      }
    ) => {
      setValues(prev => {
        const next = mergeExtractedApplicationValues(prev, extracted)
        const ids = next.experiences
          .map(experience => experience.userExperienceId)
          .filter((id): id is string => !!id)
        previousExperienceIdsRef.current = [...new Set([...previousExperienceIdsRef.current, ...ids])]
        return next
      })
      setAboutYou(prev => {
        const firstName = fillBlank(prev.firstName, extracted.firstName)
        const lastName = fillBlank(prev.lastName, extracted.lastName)
        const locationTitle = fillBlank(prev.locationTitle, extracted.location)
        const locationPlace =
          prev.locationPlace || (locationTitle ? { placeId: 'default', title: locationTitle } : null)
        if (
          firstName === prev.firstName &&
          lastName === prev.lastName &&
          locationTitle === prev.locationTitle
        ) {
          return prev
        }
        aboutDirtyRef.current = true
        return { ...prev, firstName, lastName, locationTitle, locationPlace }
      })
      dirtyRef.current = true
      setError(null)
      try {
        await Promise.all([refetchCurrentUser(), userProfileQuery.refetch()])
      } catch {
        // Form state is already merged; a refetch miss should not undo the extract.
      }
    },
    [refetchCurrentUser, userProfileQuery]
  )

  const setValue = useCallback(
    <K extends keyof AdvisorApplicationValues>(key: K, value: AdvisorApplicationValues[K]) => {
      setValues(prev => {
        const current = prev[key]
        if (Array.isArray(current) && Array.isArray(value) && key !== 'experiences') {
          if (sameStringList(current as string[], value as string[])) {
            return prev
          }
        }
        if (current === value) {
          return prev
        }
        dirtyRef.current = true
        return { ...prev, [key]: value }
      })
      setError(null)
    },
    []
  )

  /**
   * Categories cap the number of expertise areas; dropping one also drops any of
   * its preset skills that no other selected category suggests.
   */
  const setCategories = useCallback((categories: string[]) => {
    const capped = categories.slice(0, MAX_CATEGORY_BLOCKS)
    setValues(prev => {
      if (sameStringList(prev.categories, capped)) {
        return prev
      }
      dirtyRef.current = true
      const stillSuggested = new Set(skillSuggestionsFor(capped))
      const droppedPresets = new Set(
        skillSuggestionsFor(prev.categories).filter(skill => !stillSuggested.has(skill))
      )
      return {
        ...prev,
        categories: capped,
        // Keep custom skills the advisor typed; only prune presets from removed categories.
        skills: prev.skills.filter(skill => !droppedPresets.has(skill)),
      }
    })
    setError(null)
  }, [])

  const setAboutValue = useCallback(<K extends keyof AboutYouValues>(key: K, value: AboutYouValues[K]) => {
    setAboutYou(prev => {
      const current = prev[key]
      if (current === value) {
        return prev
      }
      aboutDirtyRef.current = true
      return { ...prev, [key]: value }
    })
    setError(null)
  }, [])

  const setLocation = useCallback((place: LocationInputPlace | null) => {
    setAboutYou(prev => {
      const locationTitle = place?.title || ''
      if (samePlace(prev.locationPlace, place) && prev.locationTitle === locationTitle) {
        return prev
      }
      aboutDirtyRef.current = true
      return { ...prev, locationPlace: place, locationTitle }
    })
    setError(null)
  }, [])

  const suggestedSkills = useMemo(() => skillSuggestionsFor(values.categories), [values.categories])

  /** Per-step completeness, used by the stepper progress and the review gate. */
  const stepComplete = useMemo(
    () => ({
      about: isAboutYouComplete(aboutYou),
      expertise: values.categories.length > 0 && values.skills.length > 0,
      background: values.headline.trim().length > 0,
      advising: values.compensationKeys.length > 0 && values.engagementStyleKeys.length > 0,
    }),
    [aboutYou, values]
  )

  const completedCount = Object.values(stepComplete).filter(Boolean).length
  const percentComplete = Math.round((completedCount / 4) * 100)

  const validate = useCallback((): string | null => {
    return (
      firstFieldError(aboutYouFieldErrors(aboutYou)) ||
      firstFieldError(expertiseFieldErrors(values)) ||
      firstFieldError(backgroundFieldErrors(values)) ||
      firstFieldError(advisingFieldErrors(values))
    )
  }, [aboutYou, values])

  const saveAboutYou = useCallback(async (): Promise<SaveResult> => {
    const problem = validateAboutYou(aboutYou)
    if (problem) {
      setError(problem)
      return { ok: false, statusKey: profile?.statusKey ?? null }
    }
    if (!aboutDirtyRef.current && !values.headline.trim()) {
      return { ok: true, statusKey: profile?.statusKey ?? null }
    }
    setError(null)
    try {
      const locationPlace = aboutYou.locationPlace
      const result = await updateProfile({
        variables: {
          input: {
            firstName: aboutYou.firstName.trim(),
            lastName: aboutYou.lastName.trim(),
            email: aboutYou.email.trim(),
            pictureUrl: aboutYou.pictureUrl,
            shortBio: clipAtWord(values.headline, HEADLINE_MAX) || null,
            ...(locationPlace && locationPlace.placeId !== 'default'
              ? {
                  location: locationPlace.title,
                  locationCity: locationPlace.city,
                  locationState: locationPlace.state,
                  locationCountryKey: locationPlace.countryISO || locationPlace.country,
                  locationLatitude: locationPlace.latitude,
                  locationLongitude: locationPlace.longitude,
                }
              : {}),
          },
        },
      })
      if (result.error) {
        setError(prettyErrorMessage(result.error))
        return { ok: false, statusKey: profile?.statusKey ?? null }
      }
      aboutDirtyRef.current = false
      await refetchCurrentUser()
      return { ok: true, statusKey: profile?.statusKey ?? null }
    } catch (err) {
      setError(prettyErrorMessage(err) || 'Could not save your profile.')
      return { ok: false, statusKey: profile?.statusKey ?? null }
    }
  }, [aboutYou, values.headline, updateProfile, refetchCurrentUser, profile?.statusKey])

  const syncUserSkills = useCallback(async () => {
    const managed = managedSkillTitles(values.categories, values.skills)
    const compensationTypeKeys = platformCompensationKeys(values.compensationKeys)
    const existing = userSkillsRef.current

    for (const title of managed) {
      const row = matchUserSkillByTitle(existing, title)
      const result = await upsertUserSkill({
        variables: {
          input: {
            userSkillId: row?.userSkillId || undefined,
            ...(row?.skillId ? { skillId: row.skillId } : {}),
            title,
            userRoleKeys: mergeAdvisorRoleKeys(row?.userRoleKeys),
            compensationTypeKeys,
          },
        },
      })
      if (result.error) {
        throw result.error
      }
    }

    for (const title of titlesToDelete(previousManagedTitlesRef.current, managed)) {
      const row = matchUserSkillByTitle(existing, title)
      if (!row?.userSkillId) {
        continue
      }
      const result = await upsertUserSkill({
        variables: {
          input: {
            userSkillId: row.userSkillId,
            deletedAt: new Date(),
          },
        },
      })
      if (result.error) {
        throw result.error
      }
    }

    previousManagedTitlesRef.current = managed
  }, [values.categories, values.skills, values.compensationKeys, upsertUserSkill])

  const syncUserExperiences = useCallback(async () => {
    const keptIds = new Set<string>()
    for (const experience of values.experiences) {
      const organizationName = experience.organizationName.trim()
      const positionDescription = experience.positionDescription.trim()
      if (!organizationName && !positionDescription && !experience.userExperienceId) {
        continue
      }
      const result = await upsertUserExperience({
        variables: {
          input: {
            userExperienceId: experience.userExperienceId,
            organizationName: organizationName || null,
            experienceTypeKey: experience.experienceTypeKey || 'job',
            positionDescription: positionDescription || null,
          },
        },
      })
      if (result.error) {
        throw result.error
      }
      const savedId = result.data?.upsertUserExperience?.userExperience?.userExperienceId
      if (savedId) {
        keptIds.add(savedId)
      } else if (experience.userExperienceId) {
        keptIds.add(experience.userExperienceId)
      }
    }

    for (const userExperienceId of previousExperienceIdsRef.current) {
      if (keptIds.has(userExperienceId)) {
        continue
      }
      const result = await upsertUserExperience({
        variables: {
          input: {
            userExperienceId,
            deletedAt: new Date(),
          },
        },
      })
      if (result.error) {
        throw result.error
      }
    }

    previousExperienceIdsRef.current = [...keptIds]
  }, [values.experiences, upsertUserExperience])

  const save = useCallback(
    async ({ submitForReview }: { submitForReview: boolean }): Promise<SaveResult> => {
      if (submitForReview) {
        const aboutResult = await saveAboutYou()
        if (!aboutResult.ok) {
          return aboutResult
        }
      }
      const problem = submitForReview ? validate() : null
      if (problem) {
        setError(problem)
        return { ok: false, statusKey: profile?.statusKey ?? null }
      }
      setError(null)
      try {
        if (!submitForReview) {
          const aboutResult = await saveAboutYou()
          if (!aboutResult.ok) {
            return aboutResult
          }
        }
        await syncUserSkills()
        await syncUserExperiences()
        const refetchedUser = await userProfileQuery.refetch()
        userSkillsRef.current = (refetchedUser.data?.viewer?.currentUser?.userSkills || []).filter(
          skill => !skill?.deletedAt
        )

        const result = await upsertAdvisorProfile({
          variables: {
            input: {
              categoryKeys: adviseTopicsToCategoryKeys(values.categories),
              skillTagTitles: derivedSkillTagTitles(values.categories, values.skills),
              headline: clipAtWord(values.headline, HEADLINE_MAX),
              notableBrands: derivedNotableBrands(values.experiences),
              advisingNote: values.note.trim().slice(0, NOTE_MAX) || null,
              preferredCompensationTypeKeys: platformCompensationKeys(values.compensationKeys),
              engagementStyleKeys: values.engagementStyleKeys,
              warmIntroKeys: values.warmIntroKeys,
              ...(submitForReview ? { submitForReview: true } : {}),
            },
          },
        })
        const refetched = await profileQuery.refetch()
        if (submitForReview) {
          await refetchCurrentUser()
        }
        if (result.error) {
          setError(prettyErrorMessage(result.error))
          return { ok: false, statusKey: readStatusKey(result, refetched, profile?.statusKey) }
        }
        return { ok: true, statusKey: readStatusKey(result, refetched, profile?.statusKey) }
      } catch (err) {
        setError(prettyErrorMessage(err) || 'Could not save your advisor profile.')
        return { ok: false, statusKey: profile?.statusKey ?? null }
      }
    },
    [
      values,
      validate,
      saveAboutYou,
      syncUserSkills,
      syncUserExperiences,
      upsertAdvisorProfile,
      profileQuery,
      userProfileQuery,
      profile?.statusKey,
      refetchCurrentUser,
    ]
  )

  /**
   * Flip a stored application to live (or keep it in review) without rewriting answers.
   * Used after a $49 upgrade on an already-submitted profile.
   */
  const resubmitForReview = useCallback(async (): Promise<SaveResult> => {
    setError(null)
    try {
      const result = await upsertAdvisorProfile({
        variables: { input: { submitForReview: true } },
      })
      const refetched = await profileQuery.refetch()
      await refetchCurrentUser()
      const statusKey = readStatusKey(result, refetched, profile?.statusKey)
      if (result.error) {
        setError(prettyErrorMessage(result.error))
        return { ok: false, statusKey }
      }
      return { ok: true, statusKey }
    } catch (err) {
      setError(prettyErrorMessage(err) || 'Could not submit your advisor profile.')
      return { ok: false, statusKey: profile?.statusKey ?? null }
    }
  }, [upsertAdvisorProfile, profileQuery, profile?.statusKey, refetchCurrentUser])

  return {
    values,
    aboutYou,
    aboutHydrated,
    setValue,
    setAboutValue,
    setLocation,
    setCategories,
    suggestedSkills,
    stepComplete,
    percentComplete,
    save,
    saveAboutYou,
    applyExtracted,
    resubmitForReview,
    saving:
      upsertMutation.loading ||
      updateProfileMutation.loading ||
      upsertUserSkillMutation.loading ||
      upsertUserExperienceMutation.loading,
    error,
    setError,
    profile,
    loading: !hydrated && (profileQuery.loading || userProfileQuery.loading || !!profile),
    isApproved: !!profile?.isApproved,
    statusKey: profile?.statusKey ?? null,
  }
}
